import React, { useState } from 'react';
import {
  Button,
  Form,
  FormField,
  Input,
  Label,
  Message,
  Segment,
} from 'semantic-ui-react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postWithAuth } from 'src/api';
import config from 'src/config';
import {
  CreateFormValues,
  GeneratedLinks,
  formatDate,
} from './downloadTokens.types';
import './DownloadTokenForm.module.css';

export function CopyableUrl({
  label,
  url,
  showUrl = true,
}: {
  label?: string;
  url: string;
  showUrl?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="download-token-form-copyable-url-wrapper">
      {label && (
        <div className="download-token-form-copyable-url-label">
          <Label horizontal>{label}</Label>
        </div>
      )}
      <Button
        compact
        basic
        size="mini"
        icon={copied ? 'check' : 'copy'}
        color={copied ? 'green' : undefined}
        title={copied ? 'Copied!' : 'Copy URL'}
        aria-label={copied ? 'Copied URL' : 'Copy URL'}
        onClick={handleCopy}
      />
      {showUrl && (
        <code className="download-token-form-copyable-url-code">{url}</code>
      )}
    </div>
  );
}

type Props = {
  generated: GeneratedLinks | null;
  onGenerated: (links: GeneratedLinks) => void;
};

export function DownloadTokenForm({ generated, onGenerated }: Props) {
  const qc = useQueryClient();

  const { control, handleSubmit, reset } = useForm<CreateFormValues>({
    defaultValues: { label: '', duration_hours: 12, max_uses_per_file: 3 },
  });

  const createToken = useMutation({
    mutationFn: (values: CreateFormValues) =>
      postWithAuth('admin/download_token', values),
    onSuccess: (data: GeneratedLinks) => {
      onGenerated(data);
      qc.invalidateQueries({ queryKey: ['admin', 'downloadTokens'] });
      reset();
    },
  });

  const onSubmit = (values: CreateFormValues) => {
    onGenerated(null!);
    createToken.mutate(values);
  };

  return (
    <Segment>
      <h4>Generate Download Links</h4>
      <Form onSubmit={handleSubmit(onSubmit)} error={createToken.isError}>
        <Form.Group widths="equal">
          <FormField required>
            <label>Label (recipient / purpose)</label>
            <Controller
              name="label"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input {...field} placeholder="e.g. Acme Corp trial" />
              )}
            />
          </FormField>
          <FormField>
            <label>Duration (hours, max 168)</label>
            <Controller
              name="duration_hours"
              control={control}
              rules={{ min: 1, max: 168 }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={168}
                  error={!!fieldState.error}
                  onChange={(_, { value }) =>
                    field.onChange(Math.min(168, Math.max(1, Number(value))))
                  }
                />
              )}
            />
          </FormField>
          <FormField>
            <label>Max downloads per file (max 20)</label>
            <Controller
              name="max_uses_per_file"
              control={control}
              rules={{ min: 1, max: 20 }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={20}
                  error={!!fieldState.error}
                  onChange={(_, { value }) =>
                    field.onChange(Math.min(20, Math.max(1, Number(value))))
                  }
                />
              )}
            />
          </FormField>
        </Form.Group>

        {createToken.isError && (
          <Message
            error
            content="Failed to generate token. Check server logs."
          />
        )}

        <Button
          primary
          type="submit"
          loading={createToken.isPending}
          disabled={createToken.isPending}
        >
          Generate Links
        </Button>
      </Form>

      {generated && (
        <Message positive className="download-token-form-success-message">
          <Message.Header>
            Links for &ldquo;{generated.label}&rdquo; - expires{' '}
            {formatDate(generated.expires_at)}
          </Message.Header>
          <div className="download-token-form-links-wrapper">
            <CopyableUrl
              label="OVA file"
              url={`${config.apiURL}/download/ova?token=${generated.id}`}
            />
            <CopyableUrl
              label="Update bundle"
              url={`${config.apiURL}/download/lpupdate?token=${generated.id}`}
            />
          </div>
        </Message>
      )}
    </Segment>
  );
}
